import { useState } from 'react';
import { KeyRound } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import {
  useOperators,
  useCreateOperator,
  useResetOperatorPassword,
  type Operator,
} from './operators.api';

export function OperatorsPage(): JSX.Element {
  const { data, isLoading } = useOperators();
  const create = useCreateOperator();
  const reset = useResetOperatorPassword();

  const [nw, setNw] = useState({ email: '', name: '', password: '' });
  const [resetId, setResetId] = useState<string | null>(null);
  const [resetPw, setResetPw] = useState('');

  const submitNew = (e: React.FormEvent) => {
    e.preventDefault();
    create.mutate(
      { email: nw.email.trim(), name: nw.name.trim(), password: nw.password },
      { onSuccess: () => setNw({ email: '', name: '', password: '' }) },
    );
  };

  return (
    <div className="space-y-6 max-w-2xl">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Operadores</h1>
        <p className="text-sm text-muted-foreground">Usuarios con acceso al back-office.</p>
      </div>

      <form onSubmit={submitNew} className="rounded-2xl border border-border bg-card p-5 shadow-sm space-y-4">
        <p className="text-sm font-medium">Nuevo operador</p>
        <div className="grid sm:grid-cols-3 gap-3">
          <div className="space-y-1.5">
            <Label>Email</Label>
            <Input type="email" value={nw.email} onChange={(e) => setNw((s) => ({ ...s, email: e.target.value }))} required />
          </div>
          <div className="space-y-1.5">
            <Label>Nombre</Label>
            <Input value={nw.name} onChange={(e) => setNw((s) => ({ ...s, name: e.target.value }))} required />
          </div>
          <div className="space-y-1.5">
            <Label>Contraseña</Label>
            <Input type="password" value={nw.password} onChange={(e) => setNw((s) => ({ ...s, password: e.target.value }))} minLength={8} required />
          </div>
        </div>
        <Button type="submit" disabled={create.isPending}>
          {create.isPending ? 'Creando…' : 'Crear operador'}
        </Button>
      </form>

      <div className="rounded-2xl border border-border bg-card divide-y shadow-sm">
        {isLoading && <p className="px-4 py-4 text-sm text-muted-foreground">Cargando…</p>}
        {(data ?? []).map((o: Operator) => (
          <div key={o.id} className="px-4 py-3.5">
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 rounded-full bg-primary/15 text-primary grid place-items-center text-sm font-semibold">
                {o.name.charAt(0).toUpperCase()}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium truncate">{o.name}</p>
                <p className="text-xs text-muted-foreground truncate">{o.email}</p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setResetId(resetId === o.id ? null : o.id);
                  setResetPw('');
                }}
              >
                <KeyRound className="h-4 w-4 mr-1.5" /> Resetear
              </Button>
            </div>
            {resetId === o.id && (
              <div className="mt-3 flex items-end gap-2">
                <div className="space-y-1.5 flex-1">
                  <Label>Nueva contraseña</Label>
                  <Input type="password" value={resetPw} onChange={(e) => setResetPw(e.target.value)} minLength={8} />
                </div>
                <Button
                  size="sm"
                  disabled={reset.isPending || resetPw.length < 8}
                  onClick={() =>
                    reset.mutate({ id: o.id, password: resetPw }, { onSuccess: () => setResetId(null) })
                  }
                >
                  Guardar
                </Button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
